import type { ReactElement } from 'react'
import type { SuggesterSectionKey, SuggesterStation } from './types'
import { SuggestionRow } from './SuggestionRow'
import { SectionIcon } from './icons'
import styles from './SuggestionSection.module.css'

/**
 * Props for {@link SuggestionSection}.
 */
export interface SuggestionSectionProps {
  /** Section key — drives the a11y id and the header icon. */
  readonly sectionKey: SuggesterSectionKey
  /** Resolved header text (e.g. "Nearby", "Favorites"). */
  readonly title: string
  /** Stations rendered in this section, in display order. */
  readonly stations: readonly SuggesterStation[]
  /** Forwarded to each {@link SuggestionRow}. */
  readonly onSelect: (station: SuggesterStation) => void
  /**
   * Forwarded to each {@link SuggestionRow} — fires when a Closed /
   * Advisory row's status composite is tapped (opens Disruption
   * Details, spec §5). Optional; Default / Nearby rows ignore it.
   */
  readonly onDisruptionTap?: (station: SuggesterStation) => void
  /**
   * When `true`, the icon + label header is omitted — the section is a
   * bare rows list. Used for the `results` state, which the Figma
   * design (node 6177:15851) renders as a plain result list with NO
   * section header. The `suggester-section-<key>` a11y id is still
   * carried on the wrapper.
   *
   * @defaultValue `false`
   */
  readonly headless?: boolean
  /** Forwarded to each {@link SuggestionRow} — "Station closed" badge. */
  readonly closedLabel?: string
  /** Forwarded to each {@link SuggestionRow} — "Service alert" badge. */
  readonly advisoryLabel?: string
}

/**
 * A labelled group of {@link SuggestionRow}s.
 *
 * By default renders a section header (icon + label, no background
 * strip) followed by the rows. With `headless`, the header is omitted —
 * a bare rows list (the `results` state per Figma 6177:15851).
 *
 * Carries the spec a11y id `suggester-section-<key>`. The header,
 * when shown, is a real `<h2>` so the list has a navigable heading
 * structure for screen readers.
 */
export function SuggestionSection({
  sectionKey,
  title,
  stations,
  onSelect,
  onDisruptionTap,
  headless = false,
  closedLabel,
  advisoryLabel,
}: SuggestionSectionProps): ReactElement {
  return (
    <section className={styles.section} data-testid={`suggester-section-${sectionKey}`}>
      {!headless && (
        <h2 className={styles.header}>
          <span className={styles.icon}>
            <SectionIcon sectionKey={sectionKey} />
          </span>
          <span className={styles.label}>{title}</span>
        </h2>
      )}
      <div className={styles.rows}>
        {stations.map((station) => (
          <SuggestionRow
            key={station.id}
            station={station}
            onSelect={onSelect}
            onDisruptionTap={onDisruptionTap}
            closedLabel={closedLabel}
            advisoryLabel={advisoryLabel}
          />
        ))}
      </div>
    </section>
  )
}
